import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components/macro'
import { alpha, Collapse, List, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'

import { useSidebar } from '../SidebarContext'

interface NestedNavItemProps {
    nestingLevel: number
    isVisible: boolean
    openParent: () => void
}

const NestedNavItemContext = React.createContext<NestedNavItemProps>({
    nestingLevel: 0,
    isVisible: true,
    openParent: () => {},
})

const navItemBaseCss = css<{ $nestingLevel: number; $isActive: boolean; $isOpen: boolean }>`
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1.5)};
    min-height: 40px;
    margin: 2px 10px;
    padding: ${({ theme, $nestingLevel, $isOpen }) =>
        $isOpen ? theme.spacing(1, 1.5, 1, 1.5 + $nestingLevel * 1.5) : theme.spacing(1)};
    border-radius: 8px;
    position: relative;
    transition: background-color 0.15s ease, color 0.15s ease;
    color: ${({ theme, $isActive, $nestingLevel }) => {
        if ($isActive) return theme.palette.primary.main
        return $nestingLevel === 0 ? theme.palette.secondary.main : theme.palette.grey[800]
    }};
    background-color: ${({ theme, $isActive }) => ($isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent')};
    font-weight: ${({ $isActive, $nestingLevel }) => {
        if ($isActive) return 600
        return $nestingLevel === 0 ? 500 : 400
    }};

    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
        width: 3px;
        height: ${({ $isActive }) => ($isActive ? '60%' : '0')};
        border-radius: 0 3px 3px 0;
        background-color: ${({ theme }) => theme.palette.primary.main};
        transition: height 0.15s ease;
    }

    &:hover {
        background-color: ${({ theme, $isActive }) =>
            $isActive ? alpha(theme.palette.primary.main, 0.14) : alpha(theme.palette.action.hover, 0.08)};
        color: ${({ theme, $isActive }) =>
            $isActive ? theme.palette.primary.main : theme.palette.secondary.main};
    }
`

const StyledNavItem = styled.div<{ $nestingLevel: number; $isActive: boolean; $isOpen: boolean }>`
    ${navItemBaseCss}
    justify-content: ${({ $isOpen }) => ($isOpen ? 'flex-start' : 'center')};
`

const IconWrapper = styled.span<{ $isActive: boolean; $nestingLevel: number }>`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 22px;
    height: 22px;

    & .MuiSvgIcon-root {
        font-size: 20px;
        color: ${({ theme, $isActive, $nestingLevel }) => {
            if ($isActive) return theme.palette.primary.main
            return $nestingLevel === 0 ? theme.palette.secondary.main : theme.palette.grey[800]
        }};
    }
`

const ItemLabel = styled(ListItemText)<{ $isOpen: boolean }>`
    flex: 1;
    margin: 0;
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    width: ${({ $isOpen }) => ($isOpen ? 'auto' : 0)};
    overflow: hidden;
    transition: opacity 0.2s ease;

    & .MuiTypography-root {
        font-size: 0.875rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`

const ExpandIcon = styled.span<{ $isOpen: boolean }>`
    display: flex;
    align-items: center;
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    width: ${({ $isOpen }) => ($isOpen ? 'auto' : 0)};
    overflow: hidden;
    color: ${({ theme }) => theme.palette.text.disabled};

    & .MuiSvgIcon-root {
        font-size: 18px;
    }
`

const StyledMenuItem = styled(MenuItem)<{ $isActive: boolean; $depth: number }>`
    font-size: 0.875rem;
    font-weight: ${({ $isActive }) => ($isActive ? 600 : 400)};
    padding-left: ${({ theme, $depth }) => theme.spacing(2 + $depth * 2)};
    color: ${({ theme, $isActive }) => ($isActive ? theme.palette.primary.main : theme.palette.text.primary)};
    background-color: ${({ theme, $isActive }) => ($isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent')};

    &:hover {
        background-color: ${({ theme, $isActive }) =>
            $isActive ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.action.hover, 0.08)};
    }
`

const MenuIconWrapper = styled.span<{ $isActive: boolean }>`
    display: inline-flex;
    align-items: center;
    margin-right: ${({ theme }) => theme.spacing(1.5)};

    & .MuiSvgIcon-root {
        font-size: 18px;
        color: ${({ theme, $isActive }) => ($isActive ? theme.palette.primary.main : theme.palette.text.secondary)};
    }
`

function isRouteActive(pathname: string, href?: string): boolean {
    if (!href) return false
    if (href === '/') return pathname === '/'
    return pathname === href || pathname.startsWith(`${href}/`)
}

function collectAllowedChildren(children: React.ReactNode): React.ReactElement<NavItemProps>[] {
    if (!children) return []
    return React.Children.toArray(children).filter(
        (child): child is React.ReactElement<NavItemProps> =>
            React.isValidElement(child) && child.props.isAllowed,
    )
}

interface FlatNavItem {
    title: string
    href: string
    icon?: React.ReactNode
    depth: number
}

function flattenNavChildren(children: React.ReactNode, depth = 0): FlatNavItem[] {
    const items: FlatNavItem[] = []
    React.Children.forEach(children, (child) => {
        if (!React.isValidElement<NavItemProps>(child) || !child.props.isAllowed) return
        if (child.props.href) {
            items.push({
                title: child.props.title,
                href: child.props.href,
                icon: child.props.icon,
                depth,
            })
        }
        if (child.props.children) {
            items.push(...flattenNavChildren(child.props.children, depth + 1))
        }
    })
    return items
}

export interface NavItemProps {
    icon?: React.ReactNode
    className?: string
    title: string
    href?: string
    isAllowed: boolean
    hideWithNoChildren?: boolean
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => unknown
    children?: React.ReactNode
}

export function NavItem({ icon, className, title, href, hideWithNoChildren, isAllowed, onClick, children }: NavItemProps) {
    const location = useLocation()
    const navigate = useNavigate()
    const ctx = useContext(NestedNavItemContext)
    const { isOpen: isSidebarOpen } = useSidebar()
    const itemRef = useRef<HTMLDivElement>(null)

    const [open, setOpen] = useState(false)
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

    const allowedChildren = useMemo(() => collectAllowedChildren(children), [children])
    const flatMenuItems = useMemo(() => flattenNavChildren(children), [children])
    const hasChildren = allowedChildren.length > 0

    const isSelfActive = isRouteActive(location.pathname, href)
    const isChildActive = useMemo(
        () => flatMenuItems.some((item) => isRouteActive(location.pathname, item.href)),
        [flatMenuItems, location.pathname],
    )
    const isHighlighted = isSelfActive && (!hasChildren || !isChildActive)
    const shouldExpand = isSelfActive || isChildActive

    const onClickHandler = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (!isSidebarOpen && hasChildren) {
                setMenuAnchor(itemRef.current)
                return
            }
            if (href) {
                navigate(href)
            } else if (hasChildren) {
                setOpen((prev) => !prev)
            }
            onClick?.(e)
        },
        [hasChildren, href, isSidebarOpen, navigate, onClick],
    )

    useEffect(() => {
        if (shouldExpand) {
            ctx.openParent()
            if (hasChildren && isSidebarOpen) {
                setOpen(true)
            }
        }
    }, [shouldExpand, hasChildren, isSidebarOpen])

    const nestedCtx = useMemo(
        () => ({
            nestingLevel: ctx.nestingLevel + 1,
            isVisible: open && isSidebarOpen,
            openParent: () => setOpen(true),
        }),
        [ctx.nestingLevel, open, isSidebarOpen],
    )

    if (
        !isAllowed ||
        !ctx.isVisible ||
        (hideWithNoChildren && !hasChildren)
    ) {
        return null
    }

    const navContent = (
        <StyledNavItem
            ref={itemRef}
            className={className}
            $nestingLevel={ctx.nestingLevel}
            $isActive={isHighlighted}
            $isOpen={isSidebarOpen}
            onClick={onClickHandler}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onClickHandler(e as unknown as React.MouseEvent<HTMLDivElement>)
                }
            }}>
            {!!icon && (
                <IconWrapper $isActive={isHighlighted} $nestingLevel={ctx.nestingLevel}>
                    {icon}
                </IconWrapper>
            )}
            <ItemLabel $isOpen={isSidebarOpen} primary={title} />
            {hasChildren && (
                <ExpandIcon $isOpen={isSidebarOpen}>{open ? <ExpandLess /> : <ExpandMore />}</ExpandIcon>
            )}
        </StyledNavItem>
    )

    return (
        <>
            {!isSidebarOpen ? (
                <Tooltip title={title} placement="right" arrow>
                    {navContent}
                </Tooltip>
            ) : (
                navContent
            )}

            {isSidebarOpen && hasChildren && (
                <Collapse in={open} timeout="auto" unmountOnExit>
                    <NestedNavItemContext.Provider value={nestedCtx}>
                        <List disablePadding>{children}</List>
                    </NestedNavItemContext.Provider>
                </Collapse>
            )}

            {!isSidebarOpen && hasChildren && (
                <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: {
                            minWidth: 220,
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                            py: 0.5,
                        },
                    }}>
                    {flatMenuItems.map((item) => {
                        const childActive = isRouteActive(location.pathname, item.href)
                        return (
                            <StyledMenuItem
                                key={item.href}
                                $isActive={childActive}
                                $depth={item.depth}
                                onClick={() => {
                                    navigate(item.href)
                                    setMenuAnchor(null)
                                }}>
                                {item.icon && <MenuIconWrapper $isActive={childActive}>{item.icon}</MenuIconWrapper>}
                                {item.title}
                            </StyledMenuItem>
                        )
                    })}
                </Menu>
            )}
        </>
    )
}
